import React, { useState, useEffect } from 'react';
import { Product } from '../types';
import { useCart } from '../context/CartContext';
import { api } from '../constants';
import Toast from '../components/Toast';

interface CatalogProps {
  type: 'LAB' | 'CLINIC';
  onNavigate: (page: string) => void;
  onSelectProduct: (product: Product) => void;
}

const Catalog: React.FC<CatalogProps> = ({ type, onNavigate, onSelectProduct }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const { addToCart } = useCart();

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
      setNotification({ message, type });
  };

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const allProducts = await api.getProducts();
        setProducts(allProducts.filter(p => p.category === type && p.isActive !== false));
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [type]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl font-bold text-slate-600">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="text-4xl font-black text-slate-900 mb-4">
            {type === 'LAB' ? 'Laboratory Solutions' : 'Clinical Solutions'}
          </h1>
          <p className="text-slate-600 text-lg">
            {type === 'LAB' 
              ? 'Professional equipment and materials for dental laboratories'
              : 'Essential supplies and equipment for dental practices'
            }
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="aspect-w-16 aspect-h-12 cursor-pointer" onClick={() => onSelectProduct(product)}>
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 
                  className="font-bold text-slate-900 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={() => onSelectProduct(product)}
                >
                  {product.name}
                </h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-3">{product.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-black text-slate-900">${product.price.toFixed(2)}</span>
                    {product.originalPrice && (
                      <span className="text-sm text-slate-400 line-through">${product.originalPrice.toFixed(2)}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">Stock: {product.stock}</span>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => onSelectProduct(product)}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-sm font-semibold"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => {
                        const success = addToCart(product);
                        if (!success) {
                            showToast(`Only ${product.stock} items available in stock.`, 'error');
                        } else {
                            showToast('Added to cart', 'success');
                        }
                    }}
                    disabled={product.stock === 0}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-slate-300 transition-colors text-sm font-semibold"
                  >
                    {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">No products found</h3>
            <p className="text-slate-600">Check back later for new products in this category.</p>
          </div>
        )}

        {notification && (
            <Toast 
                message={notification.message} 
                type={notification.type} 
                onClose={() => setNotification(null)} 
            />
        )}
      </div>
    </div>
  );
};

export default Catalog;
